import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MadCloudResult } from '@common/http/models/mad-cloud-result.model';
import { PageFilter } from '@common/paged-data/types/page-filter.type';
import { Page } from '@common/paged-data/types/page.type';
import { ReviewApplyOption } from '@common/reviews/enums/review-apply-option.enum';
import { ReviewFileStatus } from '@common/reviews/enums/review-file-status.enum';
import { ReviewPackageStatus } from '@common/reviews/enums/review-package-status.enum';
import { ReviewApplyPackageRequestBody } from '@common/reviews/models/review-apply-package-request-body.model';
import { ReviewApplyStateResponse } from '@common/reviews/models/review-apply-state-response.model';
import { ReviewFile } from '@common/reviews/models/review-file.model';
import { CollabApiService } from '@portal-core/collab/services/collab-api.service';
import { CollectionServiceBase } from '@portal-core/data/collection/services/collection.service.base';
import { DataService } from '@portal-core/data/common/services/data.service';
import { ReviewPackageTocModel } from '@portal-core/reviews/models/review-package-toc.model';
import { ReviewFilesService } from '@portal-core/reviews/review-files/services/review-files.service';
import { ReviewPackageConfirmCompleteDialogComponent } from '@portal-core/reviews/review-packages/components/review-package-confirm-complete-dialog/review-package-confirm-complete-dialog.component';
import { ReviewPackageResolveChangesDialogComponent, ReviewPackageResolveChangesDialogData } from '@portal-core/reviews/review-packages/components/review-package-resolve-changes-dialog/review-package-resolve-changes-dialog.component';
import { ReviewPackageWorkspaceMergeProgressDialogComponent, ReviewPackageWorkspaceMergeProgressDialogData, ReviewPackageWorkspaceMergeProgressDialogResult } from '@portal-core/reviews/review-packages/components/review-package-workspace-merge-progress-dialog/review-package-workspace-merge-progress-dialog.component';
import { CreateReviewPackage } from '@portal-core/reviews/review-packages/models/create-review-package.model';
import { ReviewPackageAnalytics } from '@portal-core/reviews/review-packages/models/review-package-analytics.model';
import { ReviewPackageFileAnalytics } from '@portal-core/reviews/review-packages/models/review-package-file-analytics.model';
import { ReviewPackageGridItem } from '@portal-core/reviews/review-packages/models/review-package-grid-item.model';
import { ReviewPackage } from '@portal-core/reviews/review-packages/models/review-package.model';
import { ReviewPackagesApiService } from '@portal-core/reviews/review-packages/services/review-packages-api.service';
import { ReviewPackagesDataService } from '@portal-core/reviews/review-packages/services/review-packages-data.service';
import { ReviewPackageOperationType } from '@portal-core/reviews/review-packages/types/review-package-operation-type.type';
import { Resettable } from '@portal-core/util/resettable.decorator';
import { filter, forkJoin, map, Observable, of, switchMap, tap } from 'rxjs';

interface ReviewPackageUnresolvedFiles {
  unresolvedFileIds: number[];
  packageFiles: ReviewFile[];
}

@Injectable({
  providedIn: 'root'
})
@Resettable()
export class ReviewPackagesService extends CollectionServiceBase<ReviewPackage> {
  constructor(
    private reviewPackagesApiService: ReviewPackagesApiService,
    private reviewPackagesDataService: ReviewPackagesDataService,
    private collabApiService: CollabApiService,
    protected dataService: DataService,
    private dialog: MatDialog,
    private reviewFilesService: ReviewFilesService
  ) {
    super(reviewPackagesDataService, dataService);
  }

  protected fetchItemById$(itemId: number): Observable<ReviewPackage> {
    return this.reviewPackagesApiService.getReviewPackageById$(itemId);
  }

  protected fetchItemsById$(itemIds: number[]): Observable<ReviewPackage[]> {
    return this.reviewPackagesApiService.getReviewPackagesByIds$(itemIds);
  }

  updateReviewPackage$(reviewPackage: Partial<ReviewPackage>, resetReviewerStatus: boolean): Observable<MadCloudResult> {
    return this.reviewPackagesApiService.updateReviewPackage$(reviewPackage, resetReviewerStatus).pipe(
      switchMap(response => {
        return this.updateItems$({ [reviewPackage.Id]: reviewPackage }).pipe(map(() => response));
      })
    );
  }

  setReviewers$(reviewPackageId: number, reviewUserIds: string[]): Observable<MadCloudResult> {
    return this.reviewPackagesApiService.setReviewers$(reviewPackageId, reviewUserIds).pipe(
      switchMap(() => this.fetchItemById$(reviewPackageId)),
      // Insert the new review data into the collection store
      switchMap(reviewPackage => this.updateItems$([reviewPackage]))
    );
  }

  bulkUpdateReviewPackageStatus$(reviewPackageIds: number[], status: ReviewPackageStatus, resetReviewerStatus: boolean): Observable<MadCloudResult<ReviewPackage[]>> {
    return this.reviewPackagesApiService.bulkUpdateReviewPackageStatus$(reviewPackageIds, status, resetReviewerStatus).pipe(
      // Insert the new review data into the collection store
      switchMap(() =>
        this.updateItems$(
          reviewPackageIds.map(reviewPackageId => {
            return {
              Id: reviewPackageId,
              Status: status
            };
          })
        )
      )
    );
  }

  createReviewPackage$(licenseId: number, projectId: number, reviewPackage: CreateReviewPackage): Observable<MadCloudResult<ReviewFile[]>> {
    return this.reviewPackagesApiService.createReviewPackage$(licenseId, projectId, reviewPackage);
  }

  deleteReviewPackages$(reviewPackageIds: number[]): Observable<MadCloudResult> {
    return this.reviewPackagesApiService.deleteReviewPackages$(reviewPackageIds).pipe(
      tap(() => {
        this.deleteItems$(reviewPackageIds);
      })
    );
  }

  getReviewPackagesPage$(
    licenseId: number,
    reviewersListLimit: number,
    filter?: PageFilter
  ): Observable<Page<ReviewPackageGridItem>> {
    return this.reviewPackagesApiService.getReviewPackagesPage$(licenseId, reviewersListLimit, filter);
  }

  getReviewsPackageAnalytics$(reviewPackageId: number, aggregateForAllUsers: boolean): Observable<ReviewPackageAnalytics> {
    return this.reviewPackagesApiService.getReviewsPackageAnalytics$(reviewPackageId, aggregateForAllUsers);
  }

  getReviewsPackageFileAnalytics$(reviewPackageId: number): Observable<ReviewPackageFileAnalytics> {
    return this.reviewPackagesApiService.getReviewsPackageFileAnalytics$(reviewPackageId);
  }

  getReviewsPackageToc$(reviewPackage: ReviewPackage, tocPath?: string): Observable<ReviewPackageTocModel> {
    const tocPathFinal = tocPath || reviewPackage.TocPath;

    if (!tocPathFinal) {
      throw new Error('TOC path is empty');
    }

    return this.reviewPackagesApiService
      .getReviewsPackageToc$(reviewPackage, tocPathFinal)
      .pipe(map(response => response.CatapultToc));
  }

  getHasAccess$(reviewPackageId: number, operationType: ReviewPackageOperationType): Observable<boolean> {
    return this.reviewPackagesApiService.getHasAccess$(reviewPackageId, operationType);
  }

  applyPackageChanges$(body: ReviewApplyPackageRequestBody): Observable<MadCloudResult<ReviewApplyStateResponse>> {
    return this.collabApiService.applyPackageChanges$(body);
  }

  /**
   * Merge the review package files to the workspace, resolving tracked changes depending on the option chosen through the review file gutter or the merge wide option selected for unresolved tracked changes
   * @param mergeUserId The user that initiates the merge
   * @param reviewPackage The review package to merge into the workspace
   * @returns The results of each review package file showing their merge status, or nothing if the process is cancelled during the workflow
   */
  mergeReviewPackageToWorkspace$(mergeUserId: string, reviewPackageId: number): Observable<ReviewPackageWorkspaceMergeProgressDialogResult | undefined> {
    // Go through the workflow to push changes to the workspace
    return forkJoin([this.getUnresolvedFiles$(reviewPackageId), this.confirmMergeReviewsToWorkspace$()]).pipe(
      // If the package status wasn't changed to merged or they don't want to apply changes to the workspace, break the chain early
      filter(([files, confirmed]) => (files?.packageFiles?.length ?? 0) != 0 && confirmed === true),
      // Prompt how the user wants to handle unresolved changes
      switchMap(([files]) => this.maybeOpenResolveChangesDialog$(files)),
      // If the user doesn't pick an action, break the chain
      filter(result => result.action !== undefined),
      // Show dialog with merge progress bar
      switchMap(result => {
        return this.dialog.open<ReviewPackageWorkspaceMergeProgressDialogComponent, ReviewPackageWorkspaceMergeProgressDialogData, ReviewPackageWorkspaceMergeProgressDialogResult>(ReviewPackageWorkspaceMergeProgressDialogComponent, {
          ...ReviewPackageWorkspaceMergeProgressDialogComponent.DialogConfig, data: {
            reviewPackageId: reviewPackageId,
            reviewPackageFiles: result.files.packageFiles,
            action: result.action as ReviewApplyOption,
            mergeUserId: mergeUserId
          }
        }).afterClosed().pipe(
          tap(result => {
            if (result?.Succeeded && result?.Result?.fileResults.every(file => file.status !== 'failed')) {
              // The web collab has already updated Madcloud. Update the client to reflect the updated package status
              this.updateItems$([{
                Id: reviewPackageId,
                Status: ReviewPackageStatus.Merged
              }]
              ).subscribe();
            }
          })
        );
      })
    );
  }

  private getUnresolvedFiles$(reviewPackageId: number): Observable<ReviewPackageUnresolvedFiles> {
    return this.reviewFilesService.getReviewFilesByPackageId$(reviewPackageId).pipe(
      switchMap(reviewFiles => {
        const nonMergedFiles = reviewFiles.filter(file => file.Status !== ReviewFileStatus.Accepted);
        return this.reviewFilesService.getUnresolvedFiles$(reviewPackageId, nonMergedFiles.map(file => file.Id)).pipe(
          map(unresolvedFileIds => { return { unresolvedFileIds: (unresolvedFileIds.Result?.reviewFileIds ?? []) as number[], packageFiles: nonMergedFiles } })
        );
      })
    );
  }

  private confirmMergeReviewsToWorkspace$(): Observable<boolean> {
    // Confirm they want to apply changes to the workspace
    return this.dialog.open<ReviewPackageConfirmCompleteDialogComponent, null, boolean>(ReviewPackageConfirmCompleteDialogComponent, { ...ReviewPackageConfirmCompleteDialogComponent.DialogConfig }).afterClosed().pipe(map(result => result ?? false));
  }

  private maybeOpenResolveChangesDialog$(files: ReviewPackageUnresolvedFiles): Observable<{ files: ReviewPackageUnresolvedFiles, action: ReviewApplyOption | undefined }> {
    // Take only the file names from the package that match the resulting ids
    const unresolvedFileNames = files.packageFiles.filter(file => files.unresolvedFileIds.includes(file.Id)).map(file => file.Name);
    if (unresolvedFileNames.length) {
      return this.dialog.open<ReviewPackageResolveChangesDialogComponent, ReviewPackageResolveChangesDialogData, ReviewApplyOption>(ReviewPackageResolveChangesDialogComponent, {
        ...ReviewPackageResolveChangesDialogComponent.DialogConfig, data: {
          unresolvedFileNames: unresolvedFileNames,
        }
      }).afterClosed().pipe(map(action => { return { files: files, action: action } }));
    } else {
      // Use any action to bypass the filter
      return of({ files: files, action: ReviewApplyOption.Accept });
    }
  }
}
